/**
 * useThreadFollowupSuggestions — React glue for the per-thread follow-up chips
 * (owner directive 2026-08-11: personalized recommendations in EVERY thread).
 *
 * Thin by design: the WHEN is the pure gate (shouldShowFollowupChips), the
 * WHAT comes from the getThreadFollowupSuggestions endpoint via the deduping
 * followupClient, and the final chip text (capability grounding + @Prest0n
 * prefix) is applied by the host at render time. This hook only decides when
 * a fresh tail is worth a fetch and holds the result for the active thread.
 *
 * Fetches are keyed on (threadId, tail) — a new fetch happens only when the
 * gate opens on a tail we have not resolved yet, so a completed Prest0n turn
 * triggers exactly one request (the client layer dedupes across remounts and
 * concurrent callers; the server caches + bills per tail-hash).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  mergeSuggestionLists,
  type MergedSuggestion,
} from '../../services/threadRecommendations/mergeSuggestions';
import {
  computeTailKey,
  fetchThreadFollowupSuggestions,
  isPendingAssistantShape,
  shouldShowFollowupChips,
  type FollowupTailMessage,
} from '../../services/threadRecommendations';

/** Minimal message view this hook needs (structural subset of ChatMessage). */
export interface FollowupSourceMessage {
  id?: string;
  text?: string;
  isUser?: boolean;
  isSystem?: boolean;
  senderId?: string | null;
  senderName?: string;
  sourceTag?: string;
  /** Interrupted/failed assistant turn (page-refresh loss, VM error card). */
  interrupted?: boolean;
  status?: string;
  regeneratePrompt?: string;
  /** Surface-plan P0 (2026-08-29): the surface-carrying fields ChatMessage
   *  already has — the tail annotates them so the distiller stops being blind
   *  to delivered cards/files (the "chips ask for a file the user already
   *  gave" bug class, and its sibling: suggesting what a standing card
   *  already offers one click away). All optional — plain texts unchanged. */
  attachments?: ReadonlyArray<unknown>;
  attachmentsSerialized?: ReadonlyArray<unknown>;
  actionButtons?: ReadonlyArray<unknown>;
  workflowActionButtonsHtml?: string;
  messageType?: string;
  title?: string;
}

/** Tail bounds mirror the server's sanitizer (it re-clamps regardless). */
const TAIL_MESSAGES = 12;
const TAIL_CHARS = 600;

/** Surface-plan P0: declarative note describing the non-text surfaces an
 *  assistant message delivered ('' for a plain text reply). Exported for tests. */
export function describeMessageSurfaces(m: FollowupSourceMessage): string {
  const notes: string[] = [];
  // MAX, never the sum (2026-09-02): the two arrays are the SAME files in two
  // representations — live File objects and their serialized records. Adding
  // them reported "delivered 4 files" in-session and "delivered 2 files" after
  // a reload (the persist boundary drops `attachments`), which changed the tail
  // text, changed the tail hash, and guaranteed a server cache MISS — and a
  // fresh paid generation — every time the user came back to the thread.
  const attachCount = Math.max(
    Array.isArray(m.attachments) ? m.attachments.length : 0,
    Array.isArray(m.attachmentsSerialized) ? m.attachmentsSerialized.length : 0,
  );
  if (attachCount > 0) notes.push(`delivered ${attachCount} file${attachCount === 1 ? '' : 's'}`);
  const hasButtons = (Array.isArray(m.actionButtons) && m.actionButtons.length > 0)
    || Boolean(String(m.workflowActionButtonsHtml || '').trim());
  if (hasButtons) {
    const kind = m.messageType === 'payment'
      ? 'a payment form'
      : m.messageType === 'appointment'
        ? 'an appointment booking widget'
        : 'an interactive card with action buttons';
    const title = String(m.title || '').replace(/\s+/g, ' ').trim().slice(0, 40);
    notes.push(`rendered ${kind}${title ? ` ("${title}")` : ''}`);
  }
  return notes.length ? `[surfaces: this message ${notes.join(' and ')}]` : '';
}

/** Build the role-labeled conversation tail the endpoint expects. Exported for tests. */
export function buildConversationTail(
  messages: ReadonlyArray<FollowupSourceMessage> | undefined,
  selfId: string | null | undefined,
): FollowupTailMessage[] {
  if (!Array.isArray(messages)) return [];
  const self = (selfId ?? '').trim();
  const tail: FollowupTailMessage[] = [];
  for (const m of messages) {
    if (!m || m.isSystem === true) continue;
    // Pending/streaming widget shapes are chrome, not conversation — their
    // ticking labels ("working · m:ss") must never mint a new tail (R17).
    if (isPendingAssistantShape(m)) continue;
    const sender = (m.senderId ?? '').trim();
    const isAssistant = m.isUser !== true || sender === 'assistant';
    // Error hygiene (owner directive 2026-08-12): an interrupted/failed turn's
    // card copy ("finished without producing the answer…") is chrome, not
    // conversation — feeding it verbatim skews the generator off the user's
    // actual goal. Mark it instead so mid-history failures still inform the
    // "get them to the answer" priority. (A failure in LAST position never
    // reaches the generator at all — the gate hands that moment to the
    // deterministic retry chip.)
    if (isAssistant && (m.interrupted === true || m.status === 'interrupted')) {
      tail.push({ role: 'assistant', text: '[this attempt failed before producing an answer]' });
      continue;
    }
    const text = String(m.text || '').replace(/\s+/g, ' ').trim().slice(0, TAIL_CHARS);
    if (isAssistant) {
      // Surface-plan P0: a card-only message (empty text, e.g. a connect or
      // approval card posted with renderPolicy 'never') used to vanish from
      // the tail entirely — the distiller then re-suggested the very thing
      // the standing card already offers. Contribute the note instead, and
      // annotate text-bearing messages within the same char budget (the
      // server re-clamps at the same cap, so an overflowing note would be
      // cut server-side otherwise).
      const surfaceNote = describeMessageSurfaces(m);
      if (!text && !surfaceNote) continue;
      const combined = !text
        ? surfaceNote
        : surfaceNote
          ? `${text.slice(0, Math.max(0, TAIL_CHARS - surfaceNote.length - 1))} ${surfaceNote}`
          : text;
      tail.push({ role: 'assistant', text: combined });
    } else if (!text) {
      continue;
    } else if (sender && self && sender !== self) {
      tail.push({ role: 'other', ...(m.senderName ? { name: String(m.senderName).slice(0, 60) } : {}), text });
    } else {
      tail.push({ role: 'user', text });
    }
  }
  return tail.slice(-TAIL_MESSAGES);
}

/**
 * REVIEW-20260824 (billing fan-out): the slice of the thread the FETCH tail is
 * built from — everything up THROUGH the last assistant turn. The render gate
 * opens after a teammate's message (owner directive 2026-08-23: the row always
 * shows after a message by Prest0n or others), but a teammate message must not
 * MINT a new tail: each viewer's tail is distinct (own messages label 'user',
 * everyone else's 'other'), so in a mention-gated group thread every
 * human↔human message billed one tier-4 distillation per OTHER viewer.
 * Anchoring keeps spend at one generation per assistant turn per viewer while
 * the row renders the held chips for that anchor. A thread with no assistant
 * turn yet anchors at its opening message — one stable tail, not one per
 * message. Exported for tests.
 */
export function anchorFetchMessages(
  messages: ReadonlyArray<FollowupSourceMessage> | undefined,
): ReadonlyArray<FollowupSourceMessage> {
  const msgs = Array.isArray(messages) ? messages : [];
  for (let i = msgs.length - 1; i >= 0; i -= 1) {
    const m = msgs[i];
    if (!m || m.isSystem === true || isPendingAssistantShape(m)) continue;
    const sender = (m.senderId ?? '').trim();
    if (m.isUser !== true || sender === 'assistant') return msgs.slice(0, i + 1);
  }
  const firstReal = msgs.findIndex((m) => m && m.isSystem !== true && !isPendingAssistantShape(m));
  return firstReal >= 0 ? msgs.slice(0, firstReal + 1) : msgs;
}

export interface ThreadFollowupSuggestionsState {
  /** Raw suggestions for the ACTIVE thread's current tail ([] while closed/loading). */
  suggestions: string[];
}

export function useThreadFollowupSuggestions(args: {
  enabled: boolean;
  threadId: string | null | undefined;
  messages: ReadonlyArray<FollowupSourceMessage> | undefined;
  selfId?: string | null;
}): ThreadFollowupSuggestionsState {
  const threadId = (args.threadId ?? '').trim();
  // `final: false` marks a provisional answer (another request held the
  // generation lease, or the server served its deterministic floor). It
  // renders — the row always shows (2026-08-23) — but never ends the retry
  // ladder: pinning the busy-path defaults as the tail's resolution was the
  // client half of the canned-chips repetition bug (owner directive
  // 2026-08-30).
  // The ACCRETIVE list (owner directive 2026-09-02). Chips arrive in two
  // stages — a fast set generated in parallel with the answer, then an
  // upgraded set generated with the answer in context — and the second must
  // never feel like the product changing its mind. Later stages take the lead
  // and earlier ones fall in behind; nothing on screen is retracted, so a chip
  // can never vanish out from under a click.
  const [resolved, setResolved] = useState<
  { key: string; chips: MergedSuggestion[]; final: boolean } | null
  >(null);

  // Anti-flicker hold (owner directive 2026-08-19): while a NEW tail's fetch
  // is in flight, keep showing the previous resolved chips for the SAME
  // thread instead of blanking the row — chips swap in place when the fresh
  // set lands. The hold never crosses threads, never survives a closed gate,
  // and never resurrects chips after the engine deliberately returned [] for
  // the current tail (that resolution matches tailKey and wins).
  const lastShownRef = useRef<{ threadId: string; suggestions: string[] } | null>(null);

  const gateOpen = args.enabled
    && Boolean(threadId)
    && shouldShowFollowupChips({ messages: args.messages, selfId: args.selfId });

  // SPLIT-BRAIN TRIPWIRE (2026-08-20 owner repro: reply rendered in a pane
  // WITHOUT its user bubble → the hasUserMessage clause silently kept chips
  // off forever). That exact combination — a completed assistant turn on top
  // with NO user message anywhere — is the cross-thread-delivery signature,
  // never a legitimate resting state (a greeting-only pane has no completed
  // ANSWER, and starters cover it). Log-only, once per thread.
  const splitBrainWarnedRef = useRef<Set<string>>(new Set());
  if (args.enabled && threadId && !gateOpen && !splitBrainWarnedRef.current.has(threadId)) {
    const msgs = Array.isArray(args.messages) ? args.messages : [];
    const hasUser = msgs.some((m) => m?.isUser === true && !(m as { isSystem?: boolean }).isSystem);
    const lastReal = [...msgs].reverse().find(
      (m) => m && !(m as { isSystem?: boolean }).isSystem && m.isUser !== true,
    ) as (typeof msgs)[number] & { sourceTag?: string; status?: string } | undefined;
    const lastIsCompletedAssistant = Boolean(
      lastReal
      && lastReal.status !== 'interrupted'
      && lastReal.sourceTag !== 'welcome'
      && !/_pending$|^prest0n_tool_start_|^prest0n_session_streaming$/.test(lastReal.sourceTag || ''),
    );
    if (!hasUser && lastIsCompletedAssistant && msgs.length > 0) {
      splitBrainWarnedRef.current.add(threadId);
      // eslint-disable-next-line no-console
      console.warn('[followupSuggestions] split-brain signature: completed assistant turn with no user bubble in the pane', {
        threadId, messageCount: msgs.length,
      });
    }
  }

  // Fetch economy (REVIEW-20260824): see anchorFetchMessages above.
  const fetchMessages = useMemo(() => anchorFetchMessages(args.messages), [args.messages]);

  const tail = useMemo(
    () => (gateOpen ? buildConversationTail(fetchMessages, args.selfId) : []),
    // messages identity changes on every store update; tail is cheap to build.
    [gateOpen, fetchMessages, args.selfId],
  );
  const tailKey = gateOpen && tail.length > 0 ? `${threadId}|${computeTailKey(threadId, tail)}` : '';

  // Track the latest wanted key so a slow response for an old tail can never
  // overwrite a newer one (classic last-write race on thread switch).
  const wantedKeyRef = useRef('');
  wantedKeyRef.current = tailKey;

  useEffect(() => {
    if (!tailKey) return;
    if (resolved?.key === tailKey && resolved.final && resolved.chips.length > 0) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    // Owner directive 2026-08-23 ("the suggested thread messages … always need
    // to display"): an empty answer is a MISS, not a resolution. A PROVISIONAL
    // answer (lease loser's held chips, the server's deterministic floor) now
    // renders immediately but keeps the ladder running too (owner directive
    // 2026-08-30) — the pre-fix code pinned busy-path defaults as final for
    // the tail's life. Retries land on the server's cache (the lease winner's
    // real chips) or regenerate past the lease window; the client cache evicts
    // every non-final answer so each attempt really re-asks. Bounded so a
    // persistently degraded backend costs four requests per tail, not a poll
    // loop; the third delay outlasts a typical generation so the winner's
    // chips are usually cached by then.
    // Reaches past the upgraded stage's own latency (measured p50 ~23s), so a
    // set generated with the answer in context still gets picked up and takes
    // the lead. These retries are cheap: once a pre-warm exists the server
    // answers them from Firestore without a model call.
    const RETRY_DELAYS_MS = [1_500, 5_000, 12_000, 20_000, 30_000];
    const attempt = (n: number): void => {
      void fetchThreadFollowupSuggestions({ threadId, tail }).then(({ suggestions, provisional, stage }) => {
        if (cancelled || wantedKeyRef.current !== tailKey) return;
        // Fold into what is already shown rather than replacing it (empty never
        // overwrites — the row must not blank mid-ladder). An unstaged answer
        // (a deterministic floor) merges as 'fast' so it can never displace a
        // real upgraded set already on screen.
        if (suggestions.length > 0) {
          setResolved((prev) => ({
            key: tailKey,
            chips: mergeSuggestionLists(
              prev?.key === tailKey ? prev.chips : [],
              suggestions,
              stage ?? 'fast',
            ),
            final: !provisional,
          }));
        }
        if (provisional && n < RETRY_DELAYS_MS.length) {
          timer = setTimeout(() => { if (!cancelled) attempt(n + 1); }, RETRY_DELAYS_MS[n]);
          return;
        }
        if (suggestions.length === 0) {
          // Out of retries with nothing usable — settle the tail (empty) so
          // the hold logic and later remounts behave exactly as before, but
          // NEVER blank chips this tail is already showing: a final network
          // blip after a provisional render must not hide the row (the
          // always-render rule, 2026-08-23).
          setResolved((prev) => (
            prev?.key === tailKey && prev.chips.length > 0
              ? prev
              : { key: tailKey, chips: [], final: true }
          ));
        }
      });
    };
    attempt(0);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // tail is derived from tailKey's inputs; keying the effect on tailKey alone
    // is what makes "one fetch per tail" true.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tailKey]);

  useEffect(() => {
    if (tailKey && resolved?.key === tailKey && resolved.chips.length > 0) {
      lastShownRef.current = { threadId, suggestions: resolved.chips.map((c) => c.text) };
    }
  }, [resolved, tailKey, threadId]);

  if (!gateOpen || !tailKey) {
    return { suggestions: [] };
  }
  if (resolved?.key === tailKey) {
    return { suggestions: resolved.chips.map((c) => c.text) };
  }
  // Fetch in flight for a fresh tail — hold the previous chips (same thread
  // only) so the row swaps instead of flashing empty.
  const held = lastShownRef.current;
  return { suggestions: held && held.threadId === threadId ? held.suggestions : [] };
}

export default useThreadFollowupSuggestions;
